<script setup>
import { reactive, ref, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import SectionLayout from 'dashboard/routes/dashboard/settings/account/components/SectionLayout.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'next/switch/Switch.vue';
import AppointmentReminderFollowUpTemplate from './AppointmentReminderFollowUpTemplate.vue';

const MAX_FOLLOW_UPS = 3;

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const contactAttributes = useMapGetter('attributes/getContactAttributes');

const isUpdating = ref(false);

const createEmptyFollowUp = () => ({
  enabled: false,
  delay_days: 0,
  delay_hours: 0,
  delay_minutes: 0,
  template_name: '',
  template_language: '',
  template_category: '',
  template_namespace: '',
  processed_params: {},
});

const state = reactive({
  enabled: false,
  followUps: [createEmptyFollowUp(), createEmptyFollowUp(), createEmptyFollowUp()],
});

const totalMinutesBefore = followUp => {
  const days = Number(followUp.delay_days) || 0;
  const hours = Number(followUp.delay_hours) || 0;
  const minutes = Number(followUp.delay_minutes) || 0;

  return days * 24 * 60 + hours * 60 + minutes;
};

const formatDelaySummary = followUp => {
  const parts = [];
  const days = Number(followUp.delay_days) || 0;
  const hours = Number(followUp.delay_hours) || 0;
  const minutes = Number(followUp.delay_minutes) || 0;

  if (days > 0) {
    parts.push(t('INBOX_MGMT.APPOINTMENT_REMINDERS.DELAY.DAYS', { count: days }));
  }
  if (hours > 0) {
    parts.push(t('INBOX_MGMT.APPOINTMENT_REMINDERS.DELAY.HOURS', { count: hours }));
  }
  if (minutes > 0) {
    parts.push(
      t('INBOX_MGMT.APPOINTMENT_REMINDERS.DELAY.MINUTES', { count: minutes })
    );
  }

  return parts.join(', ') || t('INBOX_MGMT.APPOINTMENT_REMINDERS.DELAY.EMPTY');
};

const isFollowUpVariableMissing = followUp => {
  if (!followUp.enabled || !followUp.template_name) return false;

  const params = followUp.processed_params || {};
  const entries = Object.entries(params);

  if (entries.length === 0) return true;

  return entries.some(([, value]) => !value?.trim?.());
};

const hasValidationErrors = computed(() =>
  state.followUps.some(followUp => {
    if (!followUp.enabled) return false;
    if (totalMinutesBefore(followUp) <= 0) return true;
    if (!followUp.template_name) return true;
    return isFollowUpVariableMissing(followUp);
  })
);

const lastLoadedConfigKey = ref(null);

const initializeState = () => {
  if (!props.inbox?.id) return;

  const configKey = JSON.stringify({
    enabled: props.inbox.appointment_reminders_enabled,
    config: props.inbox.appointment_reminder_config,
  });

  if (lastLoadedConfigKey.value === configKey) return;
  lastLoadedConfigKey.value = configKey;

  state.enabled = props.inbox.appointment_reminders_enabled || false;

  const followUps = props.inbox.appointment_reminder_config?.follow_ups;
  if (!Array.isArray(followUps) || followUps.length === 0) {
    state.followUps = [
      createEmptyFollowUp(),
      createEmptyFollowUp(),
      createEmptyFollowUp(),
    ];
    return;
  }

  state.followUps = Array.from({ length: MAX_FOLLOW_UPS }, (_, index) => {
    const item = followUps[index] || {};
    const processedParams = item.processed_params || {};

    return {
      enabled: item.enabled || false,
      delay_days: item.delay_days || 0,
      delay_hours: item.delay_hours || 0,
      delay_minutes: item.delay_minutes || 0,
      template_name: item.template_name || '',
      template_language: item.template_language || '',
      template_category: item.template_category || '',
      template_namespace: item.template_namespace || '',
      processed_params: { ...processedParams },
    };
  });
};

const updateFollowUp = (index, patch) => {
  Object.assign(state.followUps[index], patch);
};

onMounted(async () => {
  if (!contactAttributes.value?.length) {
    store.dispatch('attributes/get');
  }

  try {
    await store.dispatch('inboxes/fetchFromNetwork');
  } catch {
    // templates sync composable still runs per inbox
  }
});

watch(
  () => [
    props.inbox?.id,
    props.inbox?.appointment_reminders_enabled,
    props.inbox?.appointment_reminder_config,
  ],
  initializeState,
  { immediate: true, deep: true }
);

const buildPayload = () => ({
  follow_ups: state.followUps.map(followUp => ({
    enabled: followUp.enabled,
    delay_days: Number(followUp.delay_days) || 0,
    delay_hours: Number(followUp.delay_hours) || 0,
    delay_minutes: Number(followUp.delay_minutes) || 0,
    minutes_before: totalMinutesBefore(followUp),
    template_name: followUp.template_name || '',
    template_language: followUp.template_language || '',
    template_category: followUp.template_category || '',
    template_namespace: followUp.template_namespace || '',
    processed_params: { ...(followUp.processed_params || {}) },
  })),
});

const saveSettings = async () => {
  if (state.followUps.some(followUp => followUp.enabled && totalMinutesBefore(followUp) <= 0)) {
    useAlert(t('INBOX_MGMT.APPOINTMENT_REMINDERS.VALIDATION.DELAY_REQUIRED'));
    return;
  }

  if (state.followUps.some(followUp => followUp.enabled && !followUp.template_name)) {
    useAlert(t('INBOX_MGMT.APPOINTMENT_REMINDERS.VALIDATION.TEMPLATE_REQUIRED'));
    return;
  }

  if (state.followUps.some(followUp => isFollowUpVariableMissing(followUp))) {
    useAlert(t('INBOX_MGMT.APPOINTMENT_REMINDERS.VALIDATION.VARIABLES_REQUIRED'));
    return;
  }

  try {
    isUpdating.value = true;

    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      appointment_reminders_enabled: state.enabled,
      appointment_reminder_config: buildPayload(),
    });

    lastLoadedConfigKey.value = JSON.stringify({
      enabled: state.enabled,
      config: buildPayload(),
    });

    useAlert(t('INBOX_MGMT.APPOINTMENT_REMINDERS.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('INBOX_MGMT.APPOINTMENT_REMINDERS.API.ERROR_MESSAGE'));
  } finally {
    isUpdating.value = false;
  }
};
</script>

<template>
  <div class="mx-8">
    <SectionLayout
      :title="$t('INBOX_MGMT.APPOINTMENT_REMINDERS.TITLE')"
      :description="$t('INBOX_MGMT.APPOINTMENT_REMINDERS.SUBTITLE')"
    >
      <template #headerActions>
        <div class="flex justify-end">
          <Switch v-model="state.enabled" />
        </div>
      </template>

      <div class="grid gap-5">
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.APPOINTMENT_REMINDERS.HELP_TEXT') }}
        </p>

        <div
          v-for="(followUp, index) in state.followUps"
          :key="index"
          class="rounded-xl border border-n-weak"
        >
          <div class="flex items-start justify-between gap-4 p-4">
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  $t('INBOX_MGMT.APPOINTMENT_REMINDERS.FOLLOW_UP.TITLE', {
                    number: index + 1,
                  })
                }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{
                  $t('INBOX_MGMT.APPOINTMENT_REMINDERS.FOLLOW_UP.DESCRIPTION')
                }}
              </span>
            </div>
            <Switch v-model="followUp.enabled" />
          </div>

          <div
            v-if="followUp.enabled"
            class="flex flex-col gap-4 border-t border-n-weak px-4 py-4"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.APPOINTMENT_REMINDERS.FOLLOW_UP.TIMING_LABEL') }}
            </p>
            <div class="grid grid-cols-3 gap-3">
              <div>
                <label class="mb-1 block text-xs text-n-slate-11">
                  {{ $t('INBOX_MGMT.APPOINTMENT_REMINDERS.DELAY.DAYS_LABEL') }}
                </label>
                <Input
                  v-model.number="followUp.delay_days"
                  type="number"
                  min="0"
                  placeholder="0"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs text-n-slate-11">
                  {{ $t('INBOX_MGMT.APPOINTMENT_REMINDERS.DELAY.HOURS_LABEL') }}
                </label>
                <Input
                  v-model.number="followUp.delay_hours"
                  type="number"
                  min="0"
                  max="23"
                  placeholder="0"
                />
              </div>
              <div>
                <label class="mb-1 block text-xs text-n-slate-11">
                  {{
                    $t('INBOX_MGMT.APPOINTMENT_REMINDERS.DELAY.MINUTES_LABEL')
                  }}
                </label>
                <Input
                  v-model.number="followUp.delay_minutes"
                  type="number"
                  min="0"
                  max="59"
                  placeholder="0"
                />
              </div>
            </div>
            <p
              class="text-sm"
              :class="
                totalMinutesBefore(followUp) <= 0
                  ? 'text-n-ruby-11'
                  : 'text-n-slate-11'
              "
            >
              {{
                $t('INBOX_MGMT.APPOINTMENT_REMINDERS.FOLLOW_UP.SUMMARY', {
                  delay: formatDelaySummary(followUp),
                })
              }}
            </p>

            <AppointmentReminderFollowUpTemplate
              :follow-up="followUp"
              :inbox="inbox"
              @update="patch => updateFollowUp(index, patch)"
            />
          </div>
        </div>

        <p class="text-sm italic text-n-slate-11">
          {{ $t('INBOX_MGMT.APPOINTMENT_REMINDERS.NOTE') }}
        </p>

        <div>
          <NextButton
            type="submit"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
            :is-loading="isUpdating"
            :disabled="hasValidationErrors"
            @click="saveSettings"
          />
        </div>
      </div>
    </SectionLayout>
  </div>
</template>

